import React, { useState, useEffect, useRef } from 'react';

const InteractiveEyes = ({ isIdle = true }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const eyeRefs = [useRef(null), useRef(null)];
  const [isBlinking, setIsBlinking] = useState(false);
  const [isLooking, setIsLooking] = useState(false);
  const [lookPosition, setLookPosition] = useState({ x: 0, y: 0 });

  // Random blinking effect when idle
  useEffect(() => {
    if (isIdle) {
      const blinkInterval = setInterval(() => {
        if (!isBlinking && Math.random() > 0.7) {
          setIsBlinking(true);
          setTimeout(() => setIsBlinking(false), 200);
        }
      }, 1500);
      
      return () => clearInterval(blinkInterval);
    }
  }, [isIdle, isBlinking]);
  
  // Random looking around when idle and no mouse movement
  useEffect(() => {
    if (isIdle) {
      const randomLookInterval = setInterval(() => {
        // Only do random looking if mouse hasn't moved recently
        if (!isLooking && Math.random() > 0.6) {
          setIsLooking(true);
          
          // Generate random look position
          const randomX = (Math.random() - 0.5) * 200;
          const randomY = (Math.random() - 0.5) * 200;
          setLookPosition({ x: randomX, y: randomY });
          
          // Reset after a while
          setTimeout(() => {
            setIsLooking(false);
          }, 2000);
        }
      }, 3000);
      
      return () => clearInterval(randomLookInterval);
    }
  }, [isIdle, isLooking]);

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isIdle) {
        setMousePosition({ x: e.clientX, y: e.clientY });
        setIsLooking(false); // Stop random looking when mouse moves
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isIdle]);

  // Calculate eye position based on mouse coordinates
  useEffect(() => {
    if (!isIdle) return; // Don't track mouse when not idle
    
    eyeRefs.forEach(eyeRef => {
      if (!eyeRef.current) return;
      
      const pupil = eyeRef.current.querySelector('.interactive-pupil');
      if (!pupil) return;
      
      const eye = eyeRef.current;
      const eyeRect = eye.getBoundingClientRect();
      const eyeCenterX = eyeRect.left + eyeRect.width / 2;
      const eyeCenterY = eyeRect.top + eyeRect.height / 2;
      
      // Use mouse position or random look position
      const targetX = isLooking ? lookPosition.x : mousePosition.x;
      const targetY = isLooking ? lookPosition.y : mousePosition.y;
      
      // Calculate direction from eye to target
      const dx = targetX - eyeCenterX;
      const dy = targetY - eyeCenterY;
      
      // Calculate angle
      const angle = Math.atan2(dy, dx);
      
      // Maximum movement distance (radius)
      const maxDistance = eyeRect.width * 0.25;
      
      // Calculate pupil movement
      let pupilX, pupilY;
      
      if (isLooking) {
        // For random looking, use direct values
        pupilX = Math.cos(angle) * maxDistance;
        pupilY = Math.sin(angle) * maxDistance;
      } else {
        // For mouse following, calculate based on distance
        const distance = Math.min(Math.hypot(dx, dy), eyeRect.width * 2);
        const movementRatio = distance / (eyeRect.width * 4);
        pupilX = Math.cos(angle) * maxDistance * Math.min(movementRatio, 1);
        pupilY = Math.sin(angle) * maxDistance * Math.min(movementRatio, 1);
      }
      
      // Apply to the pupil element with smoother animation
      pupil.style.transition = isLooking ? 'transform 0.5s ease-out' : 'transform 0.1s ease-out';
      pupil.style.transform = `translate(${pupilX}px, ${pupilY}px)`;
    });
  }, [mousePosition, isIdle, isLooking, lookPosition]);

  return (
    <div className="interactive-eyes-container" ref={containerRef}>
      <div className={`interactive-face ${isIdle ? 'idle' : 'analyzing'}`}>
        <div className="interactive-eyes">
          <div 
            className={`interactive-eye ${isBlinking ? 'blinking' : ''}`}
            ref={eyeRefs[0]}
          >
            <div className="interactive-pupil"></div>
            <div className="interactive-eyelid"></div>
          </div>
          <div 
            className={`interactive-eye ${isBlinking ? 'blinking' : ''}`}
            ref={eyeRefs[1]}
          >
            <div className="interactive-pupil"></div>
            <div className="interactive-eyelid"></div>
          </div>
        </div>
        <div className="interactive-message">
          {isIdle ? "Upload a plant image to get started" : ""}
        </div>
      </div>
    </div>
  );
};

export default InteractiveEyes;